/* La UBICACIÓN de los renglones que se agregan desde Torrecillas.
 *
 * Lautaro Díaz (Satelital), 21/10/2026: «Al agregar a la planilla las
 * Torrecillas o las cargas diarias de Gula, que también ponga la posición
 * actual». Hasta ese día «Agregar … a la planilla» (Torrecillas → ARRIBOS
 * BITREN) y «Agregar … a SALIDAS BITREN» (las salidas de Gula) dejaban
 * UBICACIÓN vacía, y la llenaba recién el job de las 08:00 del día
 * siguiente.
 *
 * ── DE DÓNDE SALE EL TEXTO ──────────────────────────────────────────────────
 *
 * Del mismo lugar que el botón «Actualizar ubicación y pernocte»:
 * `/api/satelital/pernoctes` de FlotaBot, con las mismas claves de TRA
 * (`claveDeTra`). Pero no se escribe `ubicacion_actual` sino
 * `ubicacion_planilla`, que el bot ya trae decidido para una celda de
 * UBICACIÓN: el lugar con el alias de Lautaro y en mayúsculas («RGD», «LUIS
 * P.BUENA»), «SIN SEÑAL» si el tractor no reporta, o null si no se sabe
 * (no se pudo leer, CAR TRUCK caído, una patente que no está en ningún
 * satelital). Es lo mismo que pone el job de las 08:00, así que un renglón
 * agregado a la tarde dice lo mismo que uno de la mañana.
 *
 * Un bot viejo —antes de `ubicacion_planilla`— no escribe nada: su
 * `ubicacion_actual` no trae los alias, y «Rio Grande, Tierra del Fuego» en
 * una columna donde él escribe «RGD» es peor que la celda vacía de siempre.
 *
 * ── QUÉ SE ESCRIBE Y QUÉ NO SE TOCA ─────────────────────────────────────────
 *
 * Sólo en los renglones que se acaban de agregar (`nuevas` de
 * `agregarAArribos` y `agregarASalidas`), y en la columna UBICACIÓN —la fija,
 * no UBICACIÓN ACTUALIZADA, que es del botón—:
 *   · el tractor sale de TRA de ese renglón, leído en la planilla de AHORA:
 *     si ya no dice lo que se agregó (él corrió algo mientras FlotaBot
 *     contestaba), ese renglón no se toca. FLT y lo vacío no se buscan, como
 *     en el botón. Un TRA tapado por una combinada de arriba no es un
 *     tractor: el renglón de abajo de un bitren de SALIDAS BITREN no se lee;
 *   · en una celda combinada se escribe SÓLO en la de arriba a la izquierda,
 *     una vez (el primer renglón gana), si la combinada es de esa columna
 *     sola y su ancla es uno de los renglones nuevos: una que agarra otra
 *     columna (UBICACIÓN con la actualizada) o que sube a un renglón que ya
 *     estaba pondría la posición de este tractor en lo de otro;
 *   · nunca encima de algo escrito, ni de una fórmula: sólo celdas vacías;
 *   · null (no se sabe) no se escribe: la celda queda vacía, como hoy.
 *
 * Todo acá es puro: lo prueban los tests con datos inventados.
 */

import { mapaCombinadas } from "./planillaCombinadas";
import { claveDeTra, encabezadoUbicacionFija, lugarParaHoja } from "./pernocteBitren";

const txt = (v) => String(v ?? "").replace(/\s+/g, " ").trim();

/* El valor de TRA de un renglón como clave de FlotaBot, o "" si no se busca
   (FLT, vacío, o tapado por una combinada que viene de arriba). */
function claveDelRenglon(hoja, enc, mapa, r) {
  const rg = mapa.get(`${r},${enc.tra}`);
  if (rg && (rg.r0 !== r || rg.c0 !== enc.tra)) return "";
  return claveDeTra(hoja.celdas?.[r]?.[enc.tra]);
}

/* Los renglones nuevos con un tractor para buscar: `[{fila, clave}]`, con
   `fila` el índice desde 0. Sin la columna UBICACIÓN, ninguno. */
export function renglonesParaUbicar(hoja, nuevas) {
  const enc = encabezadoUbicacionFija(hoja);
  if (!enc) return [];
  const mapa = mapaCombinadas(hoja.combinadas);
  return [...new Set(nuevas || [])]
    .filter((r) => r > enc.fila)
    .map((fila) => ({ fila, clave: claveDelRenglon(hoja, enc, mapa, fila) }))
    .filter((x) => x.clave);
}

/* Las claves que se le piden a FlotaBot: sin repetir. */
export const clavesDeRenglones = (renglones) => [...new Set((renglones || []).map((x) => x.clave))];

/* Si FlotaBot ya trae `ubicacion_planilla`: alcanza con que una unidad
   tenga el campo (aunque sea null). Sin ninguna, es el bot de antes. */
export const traeUbicacionPlanilla = (unidades) =>
  Object.values(unidades || {}).some((u) => u && typeof u === "object" && "ubicacion_planilla" in u);

/* Escribe `ubicacion_planilla` en la UBICACIÓN de cada renglón de
   `renglones` (de `renglonesParaUbicar`) sobre `hoja`, la de ahora.
   Devuelve `{hoja, escritas}`, con `escritas` = `[{fila, clave, ubicacion}]`
   (la fila de la celda, desde 0); la hoja que entra no se toca. */
export function ponerUbicacion(hoja, renglones, unidades) {
  const enc = encabezadoUbicacionFija(hoja);
  if (!enc || !renglones?.length) return { hoja, escritas: [] };
  const mapa = mapaCombinadas(hoja.combinadas);
  const nuevas = new Set(renglones.map((x) => x.fila));
  const celdas = (hoja.celdas || []).map((r) => [...(r || [])]);
  const usadas = new Set();
  const escritas = [];
  for (const { fila, clave } of renglones) {
    if (!celdas[fila] || claveDelRenglon({ celdas }, enc, mapa, fila) !== clave) continue;
    const u = unidades?.[clave];
    // Una zona con sentido («SALIO DE DORREGO», ticket 506) va con el de
    // esta hoja: al sur en SALIDAS, a Bs As en ARRIBOS (`lugarParaHoja`).
    const lugar = lugarParaHoja(u, "ubicacion_planilla", hoja?.nombre);
    if (!lugar) continue;
    // La ancla de su combinada de una columna; con otra columna adentro, nada.
    const rg = mapa.get(`${fila},${enc.ubic}`);
    if (rg && (rg.c0 !== enc.ubic || rg.c1 !== enc.ubic)) continue;
    const r = rg ? rg.r0 : fila;
    if (!nuevas.has(r) || usadas.has(r)) continue;
    usadas.add(r);
    if (txt(celdas[r][enc.ubic])) continue;
    while (celdas[r].length <= enc.ubic) celdas[r].push("");
    celdas[r][enc.ubic] = lugar;
    escritas.push({ fila: r, clave, ubicacion: lugar });
  }
  return escritas.length ? { hoja: { ...hoja, celdas }, escritas } : { hoja, escritas };
}
